import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  FiBarChart2,
  FiChevronRight,
  FiArrowLeft,
} from "react-icons/fi";

import "./style.css";

import { getSelectedDataRecord } from "../../../Store/selectedDataRecordSlice";

const DashboardList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedProcess, category } = location.state || {};

  const { data, loading, error } = useSelector(
    (state) => state.selectedDataRecord
  );

  useEffect(() => {
    if (selectedProcess && category) {
      dispatch(
        getSelectedDataRecord({
          selectedProcess,
          category,
        })
      );
    }
  }, [dispatch, selectedProcess, category]);

  const formatName = (name) =>
    name
      ?.toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const getCardGradient = (index) => {
    const gradients = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-emerald-500 to-emerald-600",
      "from-orange-500 to-orange-600",
      "from-red-500 to-red-600",
      "from-indigo-500 to-indigo-600",
    ];

    return gradients[index % gradients.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

          <p className="text-gray-600 font-medium">
            Loading dashboards...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div className="relative bg-gradient-to-r from-[#36096d] via-[#4a0e8f] to-[#37d5d6] text-white rounded-2xl shadow-xl border-b-4 border-[#37d5d6] overflow-hidden mb-6">
        <div className="absolute inset-0 bg-gradient-to-r from-[#36096d]/20 via-[#37d5d6]/10 to-[#36096d]/20 animate-pulse"></div>

        <div className="relative p-5 z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-2xl font-bold text-white">
                Dashboard List
              </h1>

              <p className="text-white/80 text-sm mt-1">
                View and access generated analytics dashboards
              </p>
            </div>

            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-white/10 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-white/20 hover:border-white/40"
            >
              <FiArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />

              Back
            </button>
          </div>
        </div>
      </div>

      {/* EMPTY STATE */}
      {data?.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <FiBarChart2 className="w-10 h-10 text-gray-400" />
          </div>

          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No dashboards found
          </h3>

          <p className="text-gray-500">
            No dashboard records are available for this process.
          </p>
        </div>
      ) : (
        // DASHBOARD CARDS
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.map((item, idx) => (
            <div
              key={idx}
              onClick={() => window.open(item.excelLink, "_blank")}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
            >
              {/* TOP GRADIENT */}
              <div
                className={`h-2 bg-gradient-to-r ${getCardGradient(idx)}`}
              ></div>

              {/* CONTENT */}
              <div className="p-6">
                {/* ICON */}
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getCardGradient(
                    idx
                  )} flex items-center justify-center mb-4 text-white`}
                >
                  <FiBarChart2 className="w-5 h-5" />
                </div>

                {/* TITLE */}
                <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-1">
                  {formatName(item.eda_name)}
                </h3>

                {/* STATUS */}
                <p className="text-sm text-gray-500 mb-4">
                  Status:
                  <span className="ml-1 font-medium text-green-600">
                    {item.edaStatus}
                  </span>
                </p>

                {/* BUTTON */}
                <div className="flex items-center justify-end">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-300">
                    <FiChevronRight className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                </div>
              </div>

              {/* DECORATIVE BG */}
              <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-gradient-to-r from-gray-50 to-transparent rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardList;